import hostCategoryClimbingImage from "@/assets/host-category-climbing.png";
import hostCategoryFishingImage from "@/assets/host-category-fishing.png";
import hostCategoryForagingImage from "@/assets/host-category-foraging.png";
import hostCategoryHikingBackpackingImage from "@/assets/host-category-hiking-backpacking.png";
import hostCategoryHuntingImage from "@/assets/host-category-hunting.png";
import hostCategoryOverlandingImage from "@/assets/host-category-overlanding.png";
import hostCategoryPaddlingRaftingImage from "@/assets/host-category-paddling-rafting.png";
import hostCategoryPhotographyImage from "@/assets/host-category-photography.png";
import hostCategorySnorkelingFreedivingImage from "@/assets/host-category-snorkeling-freediving.png";
import hostCategorySnowWinterSportsImage from "@/assets/host-category-snow-winter-sports.png";
import hostCategoryWildernessSurvivalImage from "@/assets/host-category-wilderness-survival.png";
import hostCategoryWildlifeBirdingImage from "@/assets/host-category-wildlife-birding.png";

/** Aligned with `api/_lib/host-compliance-categories.js` */
export type HostComplianceHostType = "individual" | "business";

export const HOST_TYPE_OPTIONS: {
  value: HostComplianceHostType;
  label: string;
  description: string;
}[] = [
  {
    value: "individual",
    label: "Individual / sole proprietor",
    description:
      "Host under your own name. Most categories are available; regulated activities may need extra review when you publish.",
  },
  {
    value: "business",
    label: "Registered business (LLC, Corp, etc.)",
    description:
      "Host as a registered company. Required for hunting listings and supports regulated outfitter-style experiences.",
  },
];

export const HOST_REGULATED_LOCKED_INDIVIDUAL_COPY =
  "Regulated experiences require a registered business account. Update your host type to unlock this category.";

export const HOST_COMPLIANCE_CATEGORY_OPTIONS: {
  value: string;
  label: string;
  description?: string;
  hint?: string;
  regulated?: boolean;
  requiresAttestation?: boolean;
  /** Cover art for onboarding / compliance category cards. */
  coverImage: string;
}[] = [
  {
    value: "foraging",
    label: "Foraging",
    description:
      "Lead wild food walks, mushroom hunts, and plant identification experiences",
    coverImage: hostCategoryForagingImage,
  },
  {
    value: "leaf_peeping",
    label: "Leaf peeping",
    description:
      "Guided fall color hikes, scenic drives, and foliage tours",
    coverImage: hostCategoryHikingBackpackingImage,
  },
  {
    value: "wildflower",
    label: "Wildflowers",
    description:
      "Wildflower walks, bloom-season hikes, and botanical tours",
    coverImage: hostCategoryWildlifeBirdingImage,
  },
  {
    value: "hiking_backpacking",
    label: "Hiking & backpacking",
    description:
      "Guided day hikes, multi-day treks, and wilderness routes",
    coverImage: hostCategoryHikingBackpackingImage,
  },
  {
    value: "wilderness_survival",
    label: "Wilderness & survival",
    description:
      "Bushcraft, fire making, shelter building, and living off the land",
    coverImage: hostCategoryWildernessSurvivalImage,
  },
  {
    value: "snow_winter_sports",
    label: "Snow & winter sports",
    description:
      "Backcountry skiing, snowshoeing, ice climbing, and winter wilderness skills",
    hint: "Regulated — guide certifications may apply",
    regulated: true,
    requiresAttestation: true,
    coverImage: hostCategorySnowWinterSportsImage,
  },
  {
    value: "climbing",
    label: "Climbing & mountaineering",
    description:
      "Rock climbing, alpine routes, and guided summit ascents",
    hint: "Regulated — guide permits and review may apply",
    regulated: true,
    requiresAttestation: true,
    coverImage: hostCategoryClimbingImage,
  },
  {
    value: "wildlife_birding",
    label: "Wildlife & birding",
    description:
      "Guided wildlife viewing, birding tours, and nature observation",
    coverImage: hostCategoryWildlifeBirdingImage,
  },
  {
    value: "photography",
    label: "Photography",
    description:
      "Lead landscape, wildlife, and nature photography experiences in the field",
    coverImage: hostCategoryPhotographyImage,
  },
  {
    value: "overlanding",
    label: "Offroading & overlanding",
    description:
      "4x4 adventures, backcountry routes, and remote wilderness access",
    coverImage: hostCategoryOverlandingImage,
  },
  {
    value: "paddling_rafting",
    label: "Paddling & rafting",
    description:
      "Whitewater rafting, kayaking, canoe tours, and river expeditions",
    hint: "Regulated — guide permits and review may apply",
    regulated: true,
    requiresAttestation: true,
    coverImage: hostCategoryPaddlingRaftingImage,
  },
  {
    value: "snorkeling_freediving",
    label: "Snorkeling & freediving",
    description:
      "Guided snorkel tours, freediving instruction, and underwater exploration",
    hint: "Regulated — guide permits and review may apply",
    regulated: true,
    requiresAttestation: true,
    coverImage: hostCategorySnorkelingFreedivingImage,
  },
  {
    value: "fishing",
    label: "Fishing",
    description: "Fly fishing, lake fishing, and guided angling experiences",
    hint: "Regulated — licenses and review may apply",
    regulated: true,
    requiresAttestation: true,
    coverImage: hostCategoryFishingImage,
  },
  {
    value: "hunting",
    label: "Hunting",
    description:
      "Guided hunts, scouting trips, and field experiences with licensed outfitters",
    hint: "Regulated — licenses and review may apply",
    regulated: true,
    requiresAttestation: true,
    coverImage: hostCategoryHuntingImage,
  },
];

const CATEGORY_LABEL_BY_VALUE: Record<string, string> = {
  ...Object.fromEntries(
    HOST_COMPLIANCE_CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
  ),
  hunting_fishing: "Hunting & fishing",
};

/** Host-selectable tiers (not `suspended`). Matches `HOST_ELIGIBILITY_TIER` in eligibility-constants. */
export type HostComplianceTierIcon = "monitor" | "building" | "outdoors";

export const HOST_COMPLIANCE_TIER_OPTIONS: {
  value: string;
  label: string;
  description: string;
  icon: HostComplianceTierIcon;
}[] = [
  {
    value: "remote_only_eligible",
    icon: "monitor",
    label: "Online / remote",
    description:
      "List online classes and workshops. In-person and field listings stay blocked until you update this choice and meet requirements.",
  },
  {
    value: "in_person_basic_eligible",
    icon: "building",
    label: "In-person workshops (basic)",
    description:
      "Classroom or private-venue style sessions where policy allows—still subject to listing checks.",
  },
  {
    value: "restricted_event_eligible",
    icon: "outdoors",
    label: "Field & regulated experiences",
    description:
      "Guided field events and categories that may require licenses, insurance, or review—subject to evidence and manual review when enabled.",
  },
];

export function formatHostCategoryLabel(value: string): string {
  const normalized = value.trim().toLowerCase();
  return CATEGORY_LABEL_BY_VALUE[normalized] ?? value;
}

export type HostComplianceLandIcon = "landmark" | "map" | "home";

/** Shown as cards under “How you'll host guests” when field tier is selected. */
export const HOST_COMPLIANCE_LAND_OPTIONS: {
  value: string;
  label: string;
  description: string;
  icon: HostComplianceLandIcon;
}[] = [
  {
    value: "federal",
    icon: "landmark",
    label: "Federal land",
    description: "National forests, BLM, and other federal public land",
  },
  {
    value: "state",
    icon: "map",
    label: "State land",
    description: "State parks, wildlife areas, and state trust land",
  },
  {
    value: "private",
    icon: "home",
    label: "Private land / venue",
    description: "Your property or other private sites you have permission to use",
  },
];

const HOST_FIELD_EXPERIENCES_TIER = "restricted_event_eligible" as const;

const HOST_ELIGIBILITY_TIER_RANK: Record<string, number> = {
  remote_only_eligible: 1,
  in_person_basic_eligible: 2,
  restricted_event_eligible: 3,
};

const HOST_SELECTABLE_TIER_VALUES = new Set(
  HOST_COMPLIANCE_TIER_OPTIONS.map((option) => option.value),
);

/** Host-facing multi-select values; falls back to legacy single `hostEligibilityTier`. */
export function normalizeHostEligibilityTiers(
  rawTiers: unknown,
  legacyTier?: string | null,
): string[] {
  const tiers: string[] = [];
  if (Array.isArray(rawTiers)) {
    for (const item of rawTiers) {
      const value = typeof item === "string" ? item.trim().toLowerCase() : "";
      if (HOST_SELECTABLE_TIER_VALUES.has(value) && !tiers.includes(value)) {
        tiers.push(value);
      }
    }
  }
  if (tiers.length > 0) {
    return tiers;
  }
  const legacy =
    typeof legacyTier === "string" ? legacyTier.trim().toLowerCase() : "";
  if (legacy && HOST_SELECTABLE_TIER_VALUES.has(legacy)) {
    return [legacy];
  }
  return [];
}

/** Highest tier for publish gates (tiers are cumulative). */
export function deriveHostEligibilityTier(tiers: string[]): string | null {
  const selectable = normalizeHostEligibilityTiers(tiers);
  if (selectable.length === 0) {
    return null;
  }
  return [...selectable].sort(
    (a, b) =>
      (HOST_ELIGIBILITY_TIER_RANK[b] ?? 0) - (HOST_ELIGIBILITY_TIER_RANK[a] ?? 0),
  )[0];
}

export function hostEligibilityTiersIncludeField(tiers: string[]): boolean {
  return tiers.includes(HOST_FIELD_EXPERIENCES_TIER);
}

export function isHostCategoryLockedForType(
  categoryValue: string,
  hostType: HostComplianceHostType | null | undefined,
): boolean {
  if (hostType !== "individual") {
    return false;
  }
  const option = HOST_COMPLIANCE_CATEGORY_OPTIONS.find(
    (c) => c.value === categoryValue,
  );
  return option?.regulated === true;
}

export function filterCategoriesForHostType(
  categories: string[],
  hostType: HostComplianceHostType | null | undefined,
): string[] {
  return normalizeCategoriesOffered(categories).filter(
    (slug) => !isHostCategoryLockedForType(slug, hostType),
  );
}

/** Stable key for autosave dirty checks (sorted slugs). */
export function serializeCategoriesOffered(categories: string[]): string {
  return normalizeCategoriesOffered(categories).sort().join(",");
}

/** Expands legacy `hunting_fishing` for UI state after loading from Firestore. */
export function normalizeCategoriesOffered(categories: string[]): string[] {
  const out: string[] = [];
  categories.forEach((raw) => {
    const slug = raw.trim().toLowerCase();
    if (!slug) return;
    if (slug === "hunting_fishing") {
      if (!out.includes("hunting")) out.push("hunting");
      if (!out.includes("fishing")) out.push("fishing");
      return;
    }
    if (!out.includes(slug)) {
      out.push(slug);
    }
  });
  return out;
}
